import { Checkbox } from "@components/ui/Checkbox"
import { RadioGroup } from "@components/ui/RadioGroup"
import { createBlock, defineBlock, InspectorControls, useInlineEditableValue } from "eddev/blocks"
import { Link } from "eddev/routing"
import { Fragment, ReactNode, useMemo, useState } from "react"

export const meta: BlockMeta = {
  title: "Event Credits List",
  icon: "forms",
  description: "Credits, from Ferve.",
  category: "text",
  tags: ["film-sidebar"],
  conditions: {
    multiple: false,
  },
}

export default defineBlock("film/film-credits", (props) => {
  const [hideCredits, setHideCredits] = useInlineEditableValue<string[]>("hideTypes", [])

  const isTFF = !!props.film?.tffCenter

  let contents = useMemo(
    () => (
      <div data-mixpanel-area="Film Sidebar Credits">
        <ul className="flex flex-col gap-5 basic-text-styles">
          {FILM_CREDITS.map((credit, i) => {
            if (hideCredits.includes(credit.label)) return null
            const value = credit.get(props.film, isTFF)
            return !!value && ((Array.isArray(value) && value.length > 0) || !Array.isArray(value)) ? (
              <li key={i} className="flex flex-col gap-2">
                <div className="type-label-caps text-grey">{credit.label}</div>
                <div className="type-body-s">{value}</div>
              </li>
            ) : null
          })}
        </ul>
      </div>
    ),
    [props.film, hideCredits],
  )

  if (env.admin) {
    contents = (
      <>
        <InspectorControls>
          <div className="px-4 flex flex-col gap-1">
            {FILM_CREDITS.map((credit, i) => {
              return (
                <Checkbox
                  key={i}
                  label={credit.label}
                  checked={!hideCredits.includes(credit.label)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setHideCredits(hideCredits.filter((v) => v !== credit.label))
                    } else {
                      setHideCredits([...hideCredits, credit.label])
                    }
                  }}
                />
              )
            })}
          </div>
        </InspectorControls>
        {contents}
      </>
    )
  }

  return contents
})

type CreditType = {
  label: string
  get: (film: BlockProps["film/film-credits"]["film"], isTFF: boolean) => ReactNode
}

type LinkSpec = { title: string; uri?: string }

function LinkList(props: { items?: LinkSpec[] }) {
  const links = props.items?.map((item, i) => {
    let link: ReactNode = item.title
    if (item.uri) link = <Link href={item.uri}>{link}</Link>
    return (
      <Fragment key={i}>
        {i > 0 && ", "} {link}
      </Fragment>
    )
  })

  return <Fragment>{links}</Fragment>
}

const queryLink = (key: string, value: string) => {
  return `/program/a-z/?${key}=${encodeURIComponent(value)}`
}

function maybeLinks<T>(values: (null | undefined | T)[] | null | undefined, func: (value: T) => LinkSpec) {
  values = values?.filter(Boolean)
  if (!values?.length) return
  const links = (values as T[]).map(func).filter((v) => !!v.title)
  if (!links?.length) return
  return <LinkList items={links} />
}

const FILM_CREDITS: CreditType[] = [
  {
    label: "Program Strand",
    get: (film, isTFF) => (isTFF ? null : maybeLinks(film?.strands, (s) => ({ title: s.title!, uri: s.uri! }))),
  },
  { label: "Original Title", get: (film) => film?.filmInfo?.originalTitle },
  { label: "Year", get: (film) => film?.filmInfo?.year },
  { label: "Classification", get: (film) => film?.eventInfo?.classification },
  { label: "Country", get: (film) => film?.filmInfo?.country?.join(", ") },
  { label: "Language", get: (film) => film?.filmInfo?.language?.join(", ") },
  { label: "Director", get: (film) => film?.credits?.director?.join(", ") },
  { label: "Producer", get: (film) => film?.credits?.producer?.join(", ") },
  { label: "Co-Producer", get: (film) => film?.credits?.coproducer?.join(", ") },
  { label: "Cast", get: (film) => film?.credits?.cast?.join(", ") },
  { label: "Screenwriter", get: (film) => film?.credits?.screenwriter?.join(", ") },
  { label: "Cinematographer", get: (film) => film?.credits?.cinematographer?.join(", ") },
  { label: "Editor", get: (film) => film?.credits?.editor?.join(", ") },
  { label: "Composer", get: (film) => film?.credits?.composer?.join(", ") },
  {
    label: "Genre",
    get: (film, isTFF) =>
      maybeLinks(film?.filmInfo?.genre, (g) => ({ title: g, uri: isTFF ? undefined : queryLink("genre[]", g) })),
  },
  { label: "Premiere", get: (film) => film?.filmInfo?.premiereStatus },
  { label: "Company Credits", get: (film) => film?.credits?.companyCredits?.join(", ") },
  {
    label: "Official Website",
    get: (film) =>
      film?.filmInfo?.officialWebsite ? (
        <a href={film?.filmInfo?.officialWebsite} target="_blank">
          Visit
        </a>
      ) : null,
  },
]

/**
  From old site:
  'Program Strand',
  'Original Title',
  'Year',
  'Classification',
  'Country',
  'Language',
  'Premiere Status',
  'Director',
  'Co-Director',
  'Producer',
  'Cast',
  'Screenwriter',
  'Co-Writer',
  'Storytellers',
  'Translators',
  'Language Consultants',
  'Cinematographer',
  'Editor',
  'Animator',
  'Premiere',
  'Genre',
  'Company Credits',
  'Australian Distributor',
  'Production Company',
  'Festival Contact',
  'Sales Agent',
  'Official Website'
 */
